import { usePlanwerte } from '@/domain/controlling/hooks';
import { MonthParams } from '../../types';
import { useParams } from 'react-router-dom';
import { AbsoluteNumberField, Abweichung, MoneyField } from '../../components';
import { useTranslation } from 'react-i18next';
import { Currency, Money } from '@/domain/controlling/entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDashed } from '@fortawesome/pro-regular-svg-icons';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toNumber } from '@/utils/toNumber/toNumber';
import dayjs from 'dayjs';

// eslint-disable-next-line max-lines-per-function
export function PrognoseAbweichungKPI({ type }: { type: 'absatz' | 'umsatz' }) {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { year, month } = useParams<MonthParams>();
  const { t } = useTranslation();
  const jahr = toNumber(year) ?? dayjs().get('year');
  const monat = toNumber(month) ?? dayjs().get('month') + 1;

  const { planwerte } = usePlanwerte({
    standortId,
    jahrMonat: { jahr, monat },
  });

  let sum = 0;
  if (type == 'umsatz') {
    planwerte.forEach((planwert) => {
      if (planwert.umsatzPlan.umsatz && planwert.umsatzPlan.umsatz.wert) {
        sum = planwert.umsatzPlan.umsatz.wert.amount + sum;
      }
    });
  } else {
    planwerte.forEach((planwert) => {
      if (planwert.absatzPlan.absatz && planwert.absatzPlan.absatz.wert) {
        sum = planwert.absatzPlan.absatz.wert + sum;
      }
    });
  }

  let prognoseSum = 0;
  if (type === 'umsatz') {
    planwerte.forEach((planwert) => {
      if (planwert.umsatzPlan.prognose) {
        prognoseSum = planwert.umsatzPlan.prognose.amount + prognoseSum;
      }
    });
  } else {
    planwerte.forEach((planwert) => {
      if (planwert.absatzPlan.prognose) {
        prognoseSum = planwert.absatzPlan.prognose + prognoseSum;
      }
    });
  }

  return (
    <div className="flex flex-col gap-2 w-1/3">
      <div className="flex gap-2 items-center">
        <FontAwesomeIcon
          icon={faCircleDashed}
          className="text-secondary-teal-500 h-3 w-3"
        />
        <div className="text-xs font-semibold text-neutrals-700">
          {type === 'umsatz'
            ? t('domain.controlling.umsatzplanung.kpi.prognoseUmsatz.label')
            : t('domain.controlling.umsatzplanung.kpi.prognoseAbsatz.label')}
        </div>
      </div>
      {type === 'umsatz' ? (
        <div className="flex flex-col gap-1">
          <MoneyField
            value={new Money({ amount: prognoseSum, currency: Currency.EUR })}
            unit={t('general.currency.EUR')}
            className="[&>div:first-child]:text-2xl [&>div:last-child]:text-base [&>div:first-child]:font-extrabold [&>div:last-child]:font-normal"
          />
          <Abweichung
            tooltipMessage={t(
              'domain.controlling.umsatzplanung.planung.tooltip.abweichungZumPlanumsatz',
            )}
            baseValue={prognoseSum}
            comparisonValue={sum}
            type="umsatz"
            unit={t('general.currency.EUR')}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <AbsoluteNumberField
            className="[&>div:first-child]:text-2xl [&>div:last-child]:text-base [&>div:first-child]:font-extrabold [&>div:last-child]:font-normal"
            value={prognoseSum}
            unit={t('domain.controlling.umsatzplanung.units.stueck')}
          />
          <Abweichung
            tooltipMessage={t(
              'domain.controlling.umsatzplanung.planung.tooltip.abweichungZumPlanabsatz',
            )}
            baseValue={prognoseSum}
            comparisonValue={sum}
            type="absatz"
            unit={t('domain.controlling.umsatzplanung.units.stueck')}
          />
        </div>
      )}
    </div>
  );
}
